import { format, formatDistanceToNowStrict, parseISO } from "date-fns";

const londonParts = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

// Converts a UTC instant to a wall-clock Date in Europe/London for display only.
function toLondon(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(londonParts.formatToParts(d).map((x) => [x.type, x.value]));
  return new Date(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
}

export const formatDate = (v) => {
  const d = toLondon(v);
  return d ? format(d, "dd MMM yyyy") : "—";
};

export const formatDateTime = (v) => {
  const d = toLondon(v);
  return d ? format(d, "dd MMM yyyy HH:mm") : "—";
};

// For date-only strings (yyyy-MM-dd) which carry no time zone.
export const formatDay = (iso) => (iso ? format(parseISO(iso), "dd MMM yyyy") : "—");
export const formatDayShort = (iso) => (iso ? format(parseISO(iso), "EEE dd MMM") : "—");

export const todayLondonISO = () => format(toLondon(new Date()), "yyyy-MM-dd");

export const formatIntervals = (intervals) =>
  intervals && intervals.length ? intervals.map((i) => `${i.start}–${i.end}`).join(", ") : "Closed";

export const ROLE_LABELS = { admin: "Admin", manager: "Manager", agent: "Agent", readonly: "Read-only" };
export const EXCEPTION_TYPE_LABELS = {
  closure: "Closure",
  special_hours: "Special hours",
  seasonal_override: "Seasonal override",
};
export const SOURCE_LABELS = {
  base: "Base hours",
  bank_holiday: "Bank holiday",
  ...EXCEPTION_TYPE_LABELS,
};

export const formatCurrency = (v, currency = "GBP") =>
  v == null ? "—" : new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(Number(v));

const LONDON = "Europe/London";
const dayKey = (d) => d.toLocaleDateString("en-GB", { timeZone: LONDON });
// List dates: today (UK) -> "HH:mm", otherwise "dd/MMM/yy" (01/Jan/26)
export const formatListDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (dayKey(d) === dayKey(new Date())) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: LONDON });
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "2-digit", timeZone: LONDON }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.day}/${p.month.slice(0, 3)}/${p.year}`;
};
export const formatFullDate = (iso) => {
  if (!iso) return "";
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: LONDON }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day} ${p.month.slice(0, 3)} ${p.year} ${p.hour}:${p.minute}`;
};

export const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  return `${formatDistanceToNowStrict(d)} ${d > new Date() ? "from now" : "ago"}`.replace(/^(\d+ \w+) from now$/, "in $1");
};
